import type { ApiClient } from './client';

// The social surface (H10-S6b), typed over the S6a routes. Follow is unilateral; a
// MUTUAL follow is a friendship (derived server-side, D81); the lobby invites from two
// lists — friends and followers — because those are exactly the people whose invites
// the recipient opted into (D80). Invites are pull-only in v1: the tray is fetched, a
// join happens over the ordinary ?room=CODE path, and dismissing is the only verb.

export interface SocialPerson {
  id: string;
  displayName: string;
  /** ms epoch of the follow edge — present on graph lists, absent on discovery rows. */
  since?: number;
}

export interface DiscoveredPerson {
  id: string;
  displayName: string;
  /** My own edge state, so a row can offer Follow / Following without a second fetch. */
  following: boolean;
  followsYou: boolean;
}

export interface RoomInvite {
  id: number;
  fromAccountId: string;
  fromDisplayName: string;
  roomCode: string;
  createdAt: number;
}

export class SocialApi {
  constructor(private readonly api: ApiClient) {}

  follow(accountId: string): Promise<void> {
    return this.api.post(`/social/follows/${encodeURIComponent(accountId)}`);
  }
  unfollow(accountId: string): Promise<void> {
    return this.api.delete(`/social/follows/${encodeURIComponent(accountId)}`);
  }
  async friends(): Promise<SocialPerson[]> {
    return (await this.api.get<{ friends: SocialPerson[] }>('/social/friends')).friends;
  }
  async followers(): Promise<SocialPerson[]> {
    return (await this.api.get<{ followers: SocialPerson[] }>('/social/followers')).followers;
  }
  async following(): Promise<SocialPerson[]> {
    return (await this.api.get<{ following: SocialPerson[] }>('/social/following')).following;
  }
  async followBacks(): Promise<SocialPerson[]> {
    return (await this.api.get<{ followBacks: SocialPerson[] }>('/social/follow-backs')).followBacks;
  }
  async search(query: string): Promise<DiscoveredPerson[]> {
    return (await this.api.get<{ results: DiscoveredPerson[] }>(`/social/search?q=${encodeURIComponent(query)}`)).results;
  }
  async recentPlayers(): Promise<DiscoveredPerson[]> {
    return (await this.api.get<{ players: DiscoveredPerson[] }>('/social/recent-players')).players;
  }
  invite(toAccountId: string, roomCode: string): Promise<void> {
    return this.api.post('/social/invites', { toAccountId, roomCode });
  }
  async invites(): Promise<RoomInvite[]> {
    return (await this.api.get<{ invites: RoomInvite[] }>('/social/invites')).invites;
  }
  dismissInvite(id: number): Promise<void> {
    return this.api.delete(`/social/invites/${encodeURIComponent(String(id))}`);
  }
}
